/** UNELTELE ASISTENTULUI — declarate și executate AICI, în editor.
 *
 *  DE CE NU PE SERVER. Editorul e în browser și acolo stau regulile: un perete
 *  mutat trage după el capetele vecinilor, golurile din el, prizele de pe fața
 *  lui, conturul, iar camerele se derivă din nou din rețeaua de ziduri — patru
 *  mii de rânduri scrise o dată și verificate de fiecare gest de mouse. Un
 *  asistent care ar schimba documentul pe server ar fi fost a doua implementare
 *  a acelorași reguli, care diverge de prima la primul zid mutat, și pe deasupra
 *  nevăzută: omul n-ar fi avut ce anula.
 *
 *  Deci asistentul apasă ACELEAȘI pârghii ca mâna. Fiecare unealtă de mai jos e
 *  un apel în `editor/model.ts` — cu istoric, cu ⌘Z, cu desenul care se mișcă
 *  sub ochii omului. Ce nu se poate face cu mouse-ul nu se poate cere nici în
 *  chat, iar salvarea rămâne pe butonul omului: asistentul nu scrie în bază.
 *
 *  FIȘIERUL ĂSTA E TOT «API-UL». Serviciul nu cunoaște nicio unealtă: primește
 *  manifestul de aici la deschiderea conversației și îl dă mai departe
 *  modelului. Nimic altceva nu se atinge — nici serviciul, nici baza, nici
 *  promptul.
 *
 *  DAR UN GEST NOU ÎN EDITOR NU MAI CERE O UNEALTĂ NOUĂ. Lista de mai jos a
 *  fost, o vreme, granița a ce se poate cere în chat: o unealtă pe gest, 29 de
 *  bucăți, și tot lipsea exact aceea de care era nevoie — se putea pune o priză
 *  și nu se putea muta un tablou. Calea generală (`editeaza_prin_cod` +
 *  `api_editorului`) răstoarnă asta: modelul primește CHIAR funcțiile de mai
 *  sus, toate, citite din sursa lor. Ce a rămas aici sunt scurtăturile care
 *  chiar plătesc — v. nota de la începutul listei.
 *
 *  CE VEDE MODELUL din document: vederea DERIVATĂ (`deriva`), nu JSON-ul brut.
 *  Derivata e ce vede și omul pe ecran — arii calculate, uși cu rolul dedus,
 *  pereți cu înălțimea implicită deja aplicată —, iar indicii ei sunt aceiași cu
 *  ai listelor din document, deci ce citește modelul poate și schimba.
 */
import { cadru, mutaPereteleSingur, osiaPeretelui, schimbaCamera,
         schimbaExteriorul, schimbaGolul, sterge, val }
  from "../viewer/tronson/editor/model";
import { mobileazaAutomat } from "../viewer/tronson/design/auto";
import { FELURI_CAMERA } from "../viewer/tronson/feluri";
import { deriva, luminaGolului } from "../viewer/tronson/doc";
import type { Camera, Gol, Perete, PlanTronson, TronsonDoc }
  from "../viewer/tronson/doc";
import type { Ales } from "../viewer/tronson/Desen";
import { ruleazaCod } from "./cod";
import { suprafata } from "./api";
import { capturaPlanului } from "./captura";
import { laMetri } from "../viewer/tronson/calc";
import type { Calc } from "../viewer/tronson/calc";
import { decupeaza } from "./imagine";

/** Ce poate cere o unealtă de la editor. Editorul îl compune o dată
 *  (`Editor.tsx`) și nu se mai atinge de el: o unealtă nouă folosește ce e aici
 *  sau cere un câmp nou, explicit. */
export type ContextEditor = {
  doc: () => TronsonDoc | null;
  /** O schimbare, ca un singur pas de istoric — aceeași cale ca a panoului.
   *
   *  `contur` spune că gestul a fost chiar pe conturul modulului (frontul,
   *  banda, brațul unui colț). Fără el, conturul ar fi fost tras înapoi de
   *  ziduri imediat ce se schimbă cifra — v. `Editor::cuCamere`. */
  aplica: (f: (d: TronsonDoc) => TronsonDoc, opt?: { contur?: boolean }) => void;
  ales: () => Ales;
  anuleaza: () => void;
  /** Codul și versiunea modulului deschis, pentru starea trimisă modelului. */
  meta: () => { cod: string; versiune?: number; stare?: string };
  /** HÂRTIA DE CALC — imaginea de sub desen, cu calibrarea ei (v.
   *  `viewer/tronson/calc.ts`). Lipsește pe ecranele care n-au așa ceva.
   *
   *  NU TRECE PRIN `aplica`, și nu din comoditate: calcul nu e în document. E
   *  foaia de pe masă, ca panoramarea și ca straturile aprinse — n-are ce
   *  căuta în istoric, iar un ⌘Z care ar da înapoi o calibrare în loc de un
   *  zid mutat ar fi exact greșeala pe care istoricul o previne. */
  calc?: () => Calc | null;
  puneCalc?: (c: Calc | null) => void;
};

export type Rezultat = {
  /** Ce citește modelul. Orice se poate serializa; `undefined` = numai imagine. */
  continut?: unknown;
  /** O captură PNG, ca `data:image/png;base64,…`. */
  imagine?: string;
  eroare?: string;
  /** Câte elemente a atins — intră în urma din baza asistentului. */
  atinse?: number;
  rezumat?: string;
};

export type Unealta = {
  nume: string;
  descriere: string;
  /** Schema JSON a parametrilor, exact cum o primește modelul. */
  parametri: Record<string, unknown>;
  /** Schimbă desenul? Uneltele marcate așa nu se dau pe un proiect la care
   *  omul are doar drept de citire — serviciul le taie din listă. */
  schimba?: boolean;
  /** DIN NUCLEU: unealta se încarcă în fiecare cerere, nu se caută.
   *
   *  Restul se AMÂNĂ (v. `asistent/unelte.py`): modelul le găsește cerându-le
   *  pe nume, iar descrierile lor nu mai intră în fiecare tur. Măsurat pe
   *  16.09.2026, cu 29 de unelte: sistemul e 5.571 de tokeni, uneltele 8.823 —
   *  adică 61 % din prefix, recitit la fiecare tur. Nucleul e lista scurtă
   *  fără de care nu se poate ține o discuție: citește, uită-te, calculează,
   *  scrie planul, anulează, șterge. */
  nucleu?: boolean;
  ruleaza: (arg: any, ctx: ContextEditor) => Rezultat | Promise<Rezultat>;
};

const r2 = (v: number) => Math.round(v * 100) / 100;
const r3 = (v: number) => Math.round(v * 1000) / 1000;

// ---------------------------------------------------------------------------
// ALEGEREA: cine sunt elementele pe care le atinge o unealtă
// ---------------------------------------------------------------------------
//
// UN SINGUR VOCABULAR pentru toate uneltele care schimbă ceva, fiindcă modelul
// nu trebuie să învețe trei feluri de a spune «ușile de interior de pe latura
// A». Câmpurile se ADUNĂ (ȘI, nu SAU): fiecare restrânge mulțimea.
//
// `indici` bate orice altceva — e felul în care modelul spune «exact astea, le-am
// citit adineauri».

export type AlegereGol = {
  indici?: number[];
  fel?: "usa" | "fereastra";
  rol?: "intrare" | "interioara";
  model?: string[];
  perete?: string[];
  latime_min_m?: number;
  latime_max_m?: number;
};

export type AlegerePerete = {
  indici?: number[];
  id?: string[];
  fel?: string[];
  material?: string[];
  pe_fatada?: boolean;
};

export type AlegereCamera = {
  indici?: number[];
  fel?: string[];
  latura?: "A" | "B";
  arie_min_m2?: number;
  arie_max_m2?: number;
};

/** Golurile alese, cu poziția lor în lista din care fac parte. */
export function alegeGoluri(plan: PlanTronson, a: AlegereGol = {}): Gol[] {
  const lista = plan.goluri.filter((g) => {
    if (a.fel && g.fel !== a.fel) return false;
    if (a.rol === "intrare" && !g.intrare) return false;
    if (a.rol === "interioara" && g.intrare) return false;
    if (a.model && !a.model.includes(g.model)) return false;
    if (a.perete && !(g.perete && a.perete.includes(g.perete))) return false;
    if (a.latime_min_m != null && g.latime < a.latime_min_m) return false;
    if (a.latime_max_m != null && g.latime > a.latime_max_m) return false;
    return true;
  });
  if (!a.indici) return lista;
  // Indicii sunt PE FEL (`doors` și `windows` sunt liste separate), deci un
  // indice fără fel e ambiguu. Când modelul dă indici, dă și felul: altfel s-ar
  // schimba și ușa 3, și fereastra 3.
  const set = new Set(a.indici);
  return lista.filter((g) => set.has(g.idx));
}

export function alegePereti(plan: PlanTronson, a: AlegerePerete = {}):
    { i: number; p: Perete }[] {
  return plan.pereti
    .map((p, i) => ({ i, p }))
    .filter(({ i, p }) => {
      if (a.indici && !a.indici.includes(i)) return false;
      if (a.id && !a.id.includes(p.id)) return false;
      if (a.fel && !a.fel.includes(p.kind)) return false;
      if (a.material && !a.material.includes(p.material)) return false;
      if (a.pe_fatada != null && p.peFatada !== a.pe_fatada) return false;
      return true;
    });
}

export function alegeCamere(plan: PlanTronson, a: AlegereCamera = {}):
    { i: number; c: Camera }[] {
  return plan.camere
    .map((c, i) => ({ i, c }))
    .filter(({ i, c }) => {
      if (a.indici && !a.indici.includes(i)) return false;
      if (a.fel && !a.fel.includes(c.kind)) return false;
      if (a.latura && c.side !== a.latura) return false;
      const arie = c.arie ?? 0;
      if (a.arie_min_m2 != null && arie < a.arie_min_m2) return false;
      if (a.arie_max_m2 != null && arie > a.arie_max_m2) return false;
      return true;
    });
}

// ---------------------------------------------------------------------------
// CITIREA: planul, în felii
// ---------------------------------------------------------------------------
//
// NU SE TRIMITE DOCUMENTUL ÎNTREG. Un modul are 18–58 KB de JSON, iar mesajele
// vechi se retrimit la fiecare tur: o citire lacomă se plătește de zece ori.
// Feliile de mai jos au exact ce trebuie ca să alegi și să verifici.

/** O felie, ca TABEL: numele coloanelor o dată, apoi rândurile.
 *
 *  Nu e cochetărie, e chiar prețul discuției. Măsurat pe o cerere adevărată
 *  (16.09.2026): o citire a pereților a intrat în conversație cu 8.000 de
 *  tokeni și a fost recitită la fiecare tur de-atunci încolo — cheile repetate
 *  pe fiecare rând erau mai bine de jumătate din ea. Scrise o dată, aceleași
 *  cifre încap în două cincimi.
 *
 *  Booleenele sunt 0/1 din același motiv. Ordinea coloanelor e cea din
 *  `campuri` și nu se schimbă în timpul unei discuții.
 */
const tabel = (campuri: string[], randuri: unknown[][]) => ({ campuri, randuri });

const b = (v: boolean | undefined) => (v ? 1 : 0);

export function feliaPlanului(plan: PlanTronson, doc: TronsonDoc, ce: string) {
  switch (ce) {
    case "pereti":
      return tabel(
        ["i", "id", "fel", "material", "grosime_m", "lungime_m", "inaltime_m",
         "orizontal", "pe_fatada", "curb", "x", "y", "w", "h"],
        plan.pereti.map((p, i) => {
          const o = osiaPeretelui(p.inel);
          const c = cadru(p.inel);
          return [i, p.id, p.kind, p.material, r3(p.grosime), r2(p.lungime),
                  r2(p.inaltime), b(o.lungPeX), b(p.peFatada), b(p.bulge !== 0),
                  r2(c.x), r2(c.y), r2(c.w), r2(c.h)];
        }));
    case "goluri":
      return tabel(
        ["i", "fel", "model", "intrare", "latime_m", "toc_m", "lumina_m",
         "inaltime_m", "balama", "parapet_m", "perete", "x", "y"],
        plan.goluri.map((g) => [
          g.idx, g.fel, g.model, b(g.intrare), r2(g.latime), r3(g.toc),
          r3(luminaGolului(g.latime, g.toc)), r2(g.inaltime),
          g.balama, g.parapet == null ? null : r2(g.parapet), g.perete,
          r2(g.c[0]), r2(g.c[1])]));
    case "camere":
      return tabel(
        ["i", "fel", "latura", "flat", "arie_m2", "arie_scrisa_m2", "afara",
         "pardoseala", "perete", "x", "y"],
        plan.camere.map((c, i) => [
          i, c.kind, c.side, c.flat ?? null,
          c.arie == null ? null : r2(c.arie),
          c.arieScrisa == null ? null : r2(c.arieScrisa), c.afara,
          c.pardoseala, c.perete, r2(c.centru[0]), r2(c.centru[1])]));
    case "mobilier":
      return tabel(
        ["i", "fel", "x", "y", "w_m", "h_m", "rot_deg"],
        plan.piese.map((f, i) => [i, f.kind, r2(f.c[0]), r2(f.c[1]),
                                  r2(f.w), r2(f.h), f.deg]));
    case "electrice":
      return tabel(
        ["i", "fel", "x", "y", "h_m", "circuit"],
        plan.electrice.map((e) => [e.idx, e.kind, r2(e.c[0]), r2(e.c[1]),
                                   e.h == null ? null : r2(e.h),
                                   e.circuit ?? null]));
    case "nucleu":
      return plan.nucleu
        ? { rect: plan.nucleu.rect.map(r2),
            parti: plan.nucleu.parti.map((p, i) => ({ i, fel: p.kind })) }
        : null;
    default: {
      // SUMARUL: de aici pornește orice discuție. Are câte elemente sunt din
      // fiecare fel și cifrele care guvernează modulul, ca modelul să știe ce
      // merită citit mai departe.
      const peFel = (lista: { kind: string }[]) => {
        const t: Record<string, number> = {};
        for (const x of lista) t[x.kind] = (t[x.kind] ?? 0) + 1;
        return t;
      };
      return {
        cod: doc.id, eticheta: doc.label ?? null, rol: doc.role,
        colt: plan.colt,
        front_nominal_m: doc.front.nominal_m,
        front_min_m: doc.front.min_m, front_max_m: doc.front.max_m,
        banda_m: doc.band_depth_m, inaltime_nivel_m: plan.nivel,
        cadru: [r2(plan.cadru.x), r2(plan.cadru.y),
                r2(plan.cadru.w), r2(plan.cadru.h)],
        camere: plan.camere.length, camere_pe_fel: peFel(plan.camere),
        pereti: plan.pereti.length, pereti_pe_fel: peFel(
          plan.pereti.map((p) => ({ kind: p.kind }))),
        usi: plan.goluri.filter((g) => g.fel === "usa").length,
        usi_de_intrare: plan.goluri.filter((g) => g.intrare).length,
        ferestre: plan.goluri.filter((g) => g.fel === "fereastra").length,
        mobilier: plan.piese.length, aparate: plan.electrice.length,
        nucleu: !!plan.nucleu,
      };
    }
  }
}

// ---------------------------------------------------------------------------
// CE S-A SCHIMBAT: măsurat pe document, nu declarat de cel care a schimbat
// ---------------------------------------------------------------------------

/** Cheile din `plan` care sunt LISTE de elemente. Ce e atins se numără pe ele. */
const LISTE = ["walls", "rooms", "doors", "windows", "furniture", "electrical",
               "outdoor"] as const;

/** Câte elemente s-au mișcat între două documente.
 *
 *  Cifra intră în urma din baza asistentului (v. `conversatie::urma`) și în
 *  ce vede omul pe apel. La uneltele înguste, «atinse» era numărul de elemente
 *  alese — se știa dinainte. La o editare prin cod nu se știe, deci se măsoară:
 *  elemente apărute, dispărute sau altfel scrise. */
export function cateAuMiscat(a: TronsonDoc, b: TronsonDoc): number {
  let n = 0;
  for (const cheie of LISTE) {
    const va = ((a.plan as Record<string, unknown>)?.[cheie] ?? []) as unknown[];
    const vb = ((b.plan as Record<string, unknown>)?.[cheie] ?? []) as unknown[];
    n += Math.abs(va.length - vb.length);
    for (let i = 0; i < Math.min(va.length, vb.length); i++) {
      if (JSON.stringify(va[i]) !== JSON.stringify(vb[i])) n++;
    }
  }
  // CE NU E LISTĂ SE NUMĂRĂ CA UNU. Frontul lărgit cu 30 cm n-a mișcat niciun
  // element, dar «atinse: 0» pe o schimbare adevărată e o cifră care minte —
  // măsurat pe `schimbaFrontul`, unde toată schimbarea e în afara listelor.
  return n + (restul(a) === restul(b) ? 0 : 1);
}

/** Documentul fără listele de elemente, ca text — cifrele care guvernează
 *  modulul și tot ce nu se numără pe bucăți. */
function restul(d: TronsonDoc): string {
  const plan = { ...(d.plan ?? {}) } as Record<string, unknown>;
  for (const cheie of LISTE) delete plan[cheie];
  return JSON.stringify({ ...d, plan });
}

/** Gestul a fost pe CONTUR? — v. nota din `ContextEditor.aplica`.
 *
 *  Contează fiindcă altfel conturul e tras înapoi de ziduri imediat ce se
 *  schimbă cifra. La uneltele înguste se știa din unealtă (`schimba_modulul` îl
 *  dă mereu); la o editare prin cod nu se poate ști decât uitându-te ce s-a
 *  schimbat. */
export function conturulSchimbat(a: TronsonDoc, b: TronsonDoc): boolean {
  const ce = (d: TronsonDoc) => JSON.stringify([
    d.front, d.band_depth_m, d.plan?.outline ?? null,
    d.plan?.measured_leg_a_m ?? null, d.plan?.notch_m ?? null]);
  return ce(a) !== ce(b);
}

/** ZIDURILE SCRISE DE MÂNĂ, nu prin pârghii — numele lor, sau lista goală.
 *
 *  DE CE E O PROBĂ, nu o rugăminte în descrierea uneltei. Un zid are DOUĂ
 *  geometrii care trebuie să spună același lucru: poligonul, din care se
 *  desenează, și AXUL, din care se măsoară lungimea (v. `doc::lungimea`) și pe
 *  care se așază cotele și apucătoarele. Pârghiile editorului le mută pe
 *  amândouă; un cod care scrie direct în `poly` mută una și lasă cealaltă unde
 *  era. Ce iese e un plan în care zidul e desenat în locul nou și măsurat în
 *  cel vechi — chiar ce s-a văzut după o lărgire de hol: cota veche și cota
 *  nouă, amândouă pe planșă.
 *
 *  Și n-ar fi singura pierdere: `mutaPeretele` duce după el capetele
 *  vecinilor, golurile din zid, prizele de pe fața lui, mobila rezemată și
 *  camerele care se derivă din rețea. Nimic din toate astea nu se întâmplă la
 *  o scriere directă, iar rezultatul e un document pe care omul nu-l poate
 *  repara decât cu ⌘Z.
 *
 *  SE JUDECĂ NUMAI ZIDURILE ATINSE DE EDITAREA ASTA. Un modul transcris poate
 *  avea de la bun început un ax care nu cade pe mijloc; nu e treaba codului de
 *  azi să răspundă pentru el.
 *
 *  Pragul e de 2 cm: rotunjirile pârghiilor mișcă milimetri, o scriere directă
 *  mișcă zeci de centimetri. */
export function zidurileScriseDeMana(a: TronsonDoc, b: TronsonDoc): string[] {
  const vechi = new Map((a.plan?.walls ?? []).map((w) => [w.id, w]));
  const rele: string[] = [];
  for (const w of b.plan?.walls ?? []) {
    const v = vechi.get(w.id);
    if (!v || JSON.stringify(v.poly) === JSON.stringify(w.poly)) continue;
    if (!w.axis || w.bulge) continue;
    const inel = w.poly.map((q) => [val(q[0]), val(q[1])] as [number, number]);
    if (inel.length < 3) continue;
    const c = cadru(inel);
    const mx = (w.axis[0][0] + w.axis[1][0]) / 2;
    const my = (w.axis[0][1] + w.axis[1][1]) / 2;
    if (Math.hypot(mx - (c.x + c.w / 2), my - (c.y + c.h / 2)) > 0.02) {
      rele.push(w.id);
    }
  }
  return rele;
}

// ---------------------------------------------------------------------------
// Uneltele
// ---------------------------------------------------------------------------

const SCHEMA_ALEGERE_GOL = {
  type: "object",
  additionalProperties: false,
  properties: {
    indici: { type: "array", items: { type: "integer" },
              description: "poziția în lista ei (uși sau ferestre); cere și `fel`" },
    fel: { type: "string", enum: ["usa", "fereastra"] },
    rol: { type: "string", enum: ["intrare", "interioara"],
           description: "«intrare» = ușa de intrare în locuință (scrisă sau dedusă)" },
    model: { type: "array", items: { type: "string" },
             description: "single, double, sliding, …" },
    perete: { type: "array", items: { type: "string" },
              description: "id-urile pereților-gazdă" },
    latime_min_m: { type: "number" },
    latime_max_m: { type: "number" },
  },
} as const;

const SCHEMA_ALEGERE_PERETE = {
  type: "object",
  additionalProperties: false,
  properties: {
    indici: { type: "array", items: { type: "integer" } },
    id: { type: "array", items: { type: "string" } },
    fel: { type: "array", items: { type: "string" },
           description: "structural, partition, facade, fire, parapet" },
    material: { type: "array", items: { type: "string" },
                description: "beton sau zidarie" },
    pe_fatada: { type: "boolean" },
  },
} as const;

const SCHEMA_ALEGERE_CAMERA = {
  type: "object",
  additionalProperties: false,
  properties: {
    indici: { type: "array", items: { type: "integer" } },
    fel: { type: "array", items: { type: "string" },
           description: "living, bedroom, kitchen, bath, hall, balcony, corridor…" },
    latura: { type: "string", enum: ["A", "B"] },
    arie_min_m2: { type: "number" },
    arie_max_m2: { type: "number" },
  },
} as const;

/** Uneltele, legate de editorul deschis. */
export function unelteEditor(ctx: ContextEditor): Unealta[] {
  /** Planul derivat de ACUM. Se cere la fiecare unealtă, nu se ține: între două
   *  unelte documentul s-a schimbat, iar o derivată veche ar fi o hartă a unei
   *  clădiri care nu mai există. */
  const planul = (): { doc: TronsonDoc; plan: PlanTronson } => {
    const doc = ctx.doc();
    if (!doc) throw new Error("Nu e niciun modul deschis.");
    return { doc, plan: deriva(doc) };
  };

  // CE A RĂMAS ȘI DE CE. Uneltele de mai jos nu sunt „cele mai folosite": sunt
  // cele care DUC CEVA ce codul n-ar avea de unde lua.
  //
  //  · ALEGEREA scrisă ca argumente. «Toate ușile de interior de pe latura A»
  //    ca trei câmpuri e mai greu de greșit decât același filtru scris de
  //    fiecare dată în cod, iar greșeala la alegere e cea care nu se vede:
  //    planul arată bine și cu o ușă de intrare făcută glisantă.
  //  · ENUMERĂRILE. `fel: "loggia"` a ajuns o dată într-un document fiindcă
  //    descrierea singură n-a oprit-o (v. proba din `unelte.test.ts`); o
  //    enumerare în schemă o oprește înainte. Vocabularele pe care le poate
  //    citi din API (`FELURI_PERETE`, `MODELE_USA`) nu mai au nevoie de unealtă.
  //  · ORDINEA care se greșește tăcut: ștergerea merge de la coadă spre cap.
  //  · DOCTRINA CASEI: bagheta mobilează după regulile biroului, nu după ce-ar
  //    inventa un cod.
  //  · CITIREA ȘI CE VEDE OMUL: feliile tabelare, captura, planul de lucru,
  //    ⌘Z.
  //
  // Restul — adăugat, mutat, unit, curbat, finisat, cifrele modulului,
  // nucleul — se face prin cod, cu pârghia pe care o apasă și mouse-ul. Deci
  // nu se mai adaugă unelte aici la fiecare gest nou din editor.
  return [
    {
      nume: "citeste_planul",
      nucleu: true,
      descriere:
        "Citește planul modulului deschis, pe felii — cele pe care NU le ai "
        + "deja: «sumar» și «camere» vin cu fiecare mesaj al omului, în "
        + "`<stare_ecran>`, și sunt de-acum, nu de la începutul discuției. "
        + "Feliile vin ca tabel — `campuri` o dată, apoi `randuri` în aceeași "
        + "ordine —, iar coloana `i` e indicele prin care se schimbă "
        + "elementul. Booleenele sunt 0/1, coordonatele în metri, la frontul "
        + "nominal.",
      parametri: {
        type: "object",
        additionalProperties: false,
        required: ["ce"],
        properties: {
          ce: { type: "string",
                enum: ["sumar", "pereti", "goluri", "camere", "mobilier",
                       "electrice", "nucleu"] },
        },
      },
      ruleaza: ({ ce }) => {
        const { doc, plan } = planul();
        return { continut: feliaPlanului(plan, doc, ce) };
      },
    },

    {
      nume: "schimba_goluri",
      descriere:
        "Schimbă ușile și ferestrele alese: modelul foii (single, double, "
        + "sliding…), lățimea golului din zid sau lumina lui, înălțimea, "
        + "parapetul, lățimea tocului, rolul "
        + "ușii și sensul de deschidere. Alege întâi cu `alege`, apoi pune ce "
        + "trebuie schimbat în `pune`. Fără `alege`, atinge TOATE golurile — "
        + "așa că restrânge întotdeauna.",
      schimba: true,
      parametri: {
        type: "object",
        additionalProperties: false,
        required: ["alege", "pune"],
        properties: {
          alege: SCHEMA_ALEGERE_GOL,
          pune: {
            type: "object",
            additionalProperties: false,
            properties: {
              model: { type: "string",
                       description: "single, double, sliding, folding, pocket…" },
              latime_m: { type: "number",
                          description: "golul din zid, de la toc la toc" },
              lumina_m: { type: "number",
                          description: "lumina ușii — golul din zid se face "
                            + "cât trebuie ca ea să iasă" },
              inaltime_m: { type: "number" },
              parapet_m: { type: "number" },
              toc_m: { type: "number", description: "lățimea tocului" },
              rol: { type: "string", enum: ["entrance", "room", "fire"],
                     description: "rolul ușii, scris în document" },
              balama: { type: "string", enum: ["start", "end"] },
              sens: { type: "integer", enum: [1, -1],
                      description: "în ce parte baleiază foaia" },
            },
          },
        },
      },
      ruleaza: ({ alege, pune }) => {
        const { plan } = planul();
        const alese = alegeGoluri(plan, alege);
        if (!alese.length) return { continut: { atinse: 0, nota: "Nimic ales." } };
        ctx.aplica((d) => alese.reduce((acc, g) => schimbaGolul(
          acc, g.fel === "usa" ? "doors" : "windows", g.idx, {
            model: pune.model, latime: pune.latime_m,
            lumina: pune.lumina_m,
            inaltime: pune.inaltime_m, parapet: pune.parapet_m,
            toc: pune.toc_m, rol: pune.rol, balama: pune.balama,
            sens: pune.sens,
          }), d));
        return {
          atinse: alese.length,
          rezumat: `${alese.length} goluri`,
          continut: { atinse: alese.length,
                      indici: alese.map((g) => ({ fel: g.fel, i: g.idx })) },
        };
      },
    },

    {
      nume: "muta_pereti",
      descriere:
        "Mută pereții aleși cu (dx, dy) metri. Se mută ca la mouse: capetele "
        + "vecinilor îi urmează, golurile și aparatele de pe ei merg cu ei, iar "
        + "camerele se derivă din nou din rețeaua de ziduri.",
      schimba: true,
      parametri: {
        type: "object",
        additionalProperties: false,
        required: ["alege", "dx_m", "dy_m"],
        properties: {
          alege: SCHEMA_ALEGERE_PERETE,
          dx_m: { type: "number" }, dy_m: { type: "number" },
        },
      },
      ruleaza: ({ alege, dx_m, dy_m }) => {
        const { plan } = planul();
        const alesi = alegePereti(plan, alege);
        if (!alesi.length) return { continut: { atinse: 0, nota: "Nimic ales." } };
        ctx.aplica((d) => alesi.reduce(
          (acc, { i }) => mutaPereteleSingur(acc, i, [dx_m, dy_m]), d));
        return {
          atinse: alesi.length, rezumat: `${alesi.length} pereți`,
          continut: { atinse: alesi.length, indici: alesi.map((x) => x.i),
                      nota: "Camerele s-au derivat din nou — indicii lor se pot "
                            + "fi schimbat. Citește din nou înainte să te "
                            + "folosești de ei." },
        };
      },
    },

    {
      nume: "schimba_camere",
      // DESCRIEREA E SCURTĂ FIINDCĂ E PLĂTITĂ LA FIECARE TUR (v. proba de preț
      // a manifestului). De ce numele e liber și felul nu — un tip nou de
      // spațiu e perechea (nume liber, fel canonic) — stă în schema
      // catalogului, lângă câmp, nu aici.
      descriere:
        "Schimbă FELUL camerelor alese și numele scris pe ele. Felul e numai "
        + "din listă — «logie» NU e un fel: un balcon rămâne `balcony`, iar "
        + "consolă sau logie se alege cu `schimba_exteriorul`. `nume` e "
        + "cuvântul de pe planșă, când camera nu se cheamă ca felul ei "
        + "(«Cramă»): așa pui un tip de spațiu pe care vocabularul nu-l are.",
      schimba: true,
      parametri: {
        type: "object",
        additionalProperties: false,
        required: ["alege"],
        properties: {
          alege: SCHEMA_ALEGERE_CAMERA,
          // ENUMERAREA E PAZA, nu descrierea. Măsurat pe API (16.09.2026): la
          // «fă balconul logie», modelul a găsit unealta asta prin căutare și a
          // chemat-o cu `fel: "loggia"` — un fel care nu există în vocabular și
          // pe care nimic nu l-ar fi oprit să ajungă în document. Lista e cea
          // din paletă, din același fișier, deci nu poate diverge de ea.
          fel: { type: "string", enum: [...FELURI_CAMERA] },
          // NUMELE E LIBER, și tocmai asta e diferența dintre el și fel: unul e
          // vocabular închis (paza de mai sus), celălalt e un cuvânt de pe
          // planșă. Un tip nou de spațiu e perechea celor două.
          nume: { type: "string", maxLength: 40 },
        },
      },
      ruleaza: ({ alege, fel, nume }) => {
        const { plan } = planul();
        if (fel == null && nume == null) {
          return { eroare: "Spune ce schimbi: `fel`, `nume`, sau amândouă." };
        }
        const alese = alegeCamere(plan, alege);
        if (!alese.length) return { continut: { atinse: 0, nota: "Nimic ales." } };
        ctx.aplica((d) => alese.reduce(
          (acc, { i }) => schimbaCamera(acc, i, {
            ...(fel != null ? { kind: fel } : {}),
            ...(nume != null ? { label: String(nume) } : {}),
          }), d));
        return { atinse: alese.length, rezumat: `${alese.length} camere`,
                 continut: { atinse: alese.length,
                             indici: alese.map((x) => x.i) } };
      },
    },

    {
      nume: "sterge_elemente",
      nucleu: true,
      descriere: "Șterge elementele alese. Indicii se schimbă după ștergere — "
        + "citește din nou înainte de altceva.",
      schimba: true,
      parametri: {
        type: "object",
        additionalProperties: false,
        required: ["fel", "indici"],
        properties: {
          fel: { type: "string",
                 enum: ["perete", "usa", "fereastra", "camera", "mobila", "aparat"] },
          indici: { type: "array", items: { type: "integer" } },
        },
      },
      ruleaza: ({ fel, indici }) => {
        const lista = [...new Set(indici as number[])].sort((a, b) => b - a);
        if (!lista.length) return { continut: { atinse: 0 } };
        const ca = (i: number): Ales =>
          fel === "usa" ? { fel: "gol", fel2: "doors", i }
          : fel === "fereastra" ? { fel: "gol", fel2: "windows", i }
          : ({ fel, i } as Ales);
        // DE LA COADĂ SPRE CAP: o ștergere scoate un element din listă, deci
        // indicii de după el se mută cu unul. Ștergând descrescător, cei
        // rămași de făcut nu se mișcă.
        ctx.aplica((d) => lista.reduce((acc, i) => sterge(acc, ca(i)), d));
        return { atinse: lista.length, rezumat: `${lista.length} ${fel}`,
                 continut: { atinse: lista.length } };
      },
    },

    // ---- BAGHETA ---------------------------------------------------------
    //
    // Mobilierul și aparatele se pun, se mută și se schimbă prin cod ca orice
    // altceva (`E.adaugaAparatInCamera`, `E.mutaAparat`, …). Ce NU se poate lua
    // din API e doctrina biroului — mașina de spălat în bucătărie, duș fără
    // cadă, priza lângă mobila care o cere —, iar aia stă în bagheta de mai
    // jos și de-aia rămâne unealtă.

    {
      nume: "mobileaza_automat",
      descriere: "Amenajează TOT modulul dintr-o mișcare: mobilează camerele "
        + "după felul lor și trage instalația peste ea. Înlocuiește ce e pus "
        + "deja, în afară de doze, dacă nu ceri altfel. Întoarce și camerele "
        + "sărite, cu motivul.",
      schimba: true,
      parametri: {
        type: "object", additionalProperties: false,
        properties: {
          pastreaza_mobilierul: {
            type: "boolean",
            description: "true = nu atinge mobilierul, pune doar instalația",
          },
        },
      },
      ruleaza: ({ pastreaza_mobilierul }) => {
        const { doc, plan } = planul();
        const r = mobileazaAutomat(doc, plan,
                                   { cuMobilier: !pastreaza_mobilierul });
        ctx.aplica(() => r.doc);
        return {
          atinse: r.piese + r.aparate,
          rezumat: `${r.piese} piese, ${r.aparate} aparate`,
          continut: { piese: r.piese, aparate: r.aparate, sarite: r.sarite },
        };
      },
    },

    // ---- SPAȚIUL EXTERIOR ------------------------------------------------
    //
    // Balconul nu e o cameră cu alt nume: se închide cu parapeți pe laturile
    // fără zid, iar felul lui (consolă sau logie) hotărăște dacă intră în
    // suprafața construită. Se adaugă prin cod (`E.adaugaBalcon`); felul lui
    // rămâne aici, fiindcă e o enumerare de două valori peste care s-a trecut
    // deja o dată.

    {
      nume: "schimba_exteriorul",
      descriere:
        "LOGIE sau BALCON în consolă, pe camerele de fel `balcony` alese. Asta "
        + "e unealta pentru «fă balconul logie» — felul camerei rămâne "
        + "`balcony`, se schimbă doar cum stă în volum. Nu e cosmetic: consola "
        + "stă în afara benzii și nu intră în suprafața construită, logia e "
        + "scobită în volum și intră.",
      schimba: true,
      parametri: {
        type: "object", additionalProperties: false, required: ["alege", "fel"],
        properties: {
          alege: SCHEMA_ALEGERE_CAMERA,
          fel: { type: "string", enum: ["balcony", "loggia"] },
        },
      },
      ruleaza: ({ alege, fel }) => {
        const alese = alegeCamere(planul().plan, alege)
          .filter(({ c }) => c.kind === "balcony");
        if (!alese.length) {
          return { continut: { atinse: 0, nota: "Nicio cameră de balcon aleasă." } };
        }
        ctx.aplica((d) => alese.reduce(
          (acc, { i }) => schimbaExteriorul(acc, i, fel), d));
        return { atinse: alese.length, rezumat: `${alese.length} spații`,
                 continut: { atinse: alese.length } };
      },
    },

    // ---- CE VEDE OMUL ȘI CE POATE LUA ÎNAPOI -----------------------------

    {
      nume: "anuleaza",
      nucleu: true,
      descriere: "Anulează ultimul pas (⌘Z). Folosește-o când ai aplicat ceva "
        + "și ai văzut, verificând, că n-a ieșit ce trebuia.",
      schimba: true,
      parametri: {
        type: "object", additionalProperties: false,
        properties: { pasi: { type: "integer", minimum: 1, maximum: 10 } },
      },
      ruleaza: ({ pasi }) => {
        const n = Math.min(Math.max(1, pasi ?? 1), 10);
        for (let k = 0; k < n; k++) ctx.anuleaza();
        return { atinse: n, rezumat: `anulat ${n}`, continut: { anulati: n } };
      },
    },

    {
      nume: "vezi_planul",
      nucleu: true,
      descriere:
        "Fă o captură a planului de pe ecran și uită-te la ea. E pentru ce nu "
        + "poate spune o cifră — ziduri intrate unul în altul, o cameră ruptă. "
        + "Nu după fiecare pas: editarea prin cod întoarce deja sumarul de "
        + "dinainte și de după, iar captura costă un tur și o imagine.",
      parametri: { type: "object", additionalProperties: false, properties: {} },
      ruleaza: async () => {
        const png = await capturaPlanului();
        return png
          ? { imagine: png, continut: "Captura planului de pe ecran." }
          : { eroare: "Nu am putut face captura (pânza nu e pe ecran)." };
      },
    },

    // ---- HÂRTIA DE CALC: imaginea de sub desen ---------------------------
    //
    // DOUĂ UNELTE, ȘI NICIUNA NU DESENEAZĂ. Desenatul are deja calea lui
    // (`editeaza_prin_cod` peste pârghiile editorului) și n-are de ce să
    // capete o a doua fiindcă sursa e o poză. Ce lipsea era ALTCEVA: să se
    // poată UITA de-aproape la imagine, și să poată AȘEZA foaia peste plan la
    // scara ei. Amândouă sunt gesturi pe care le face și mâna cu o hârtie de
    // calc pe masă — apropii ochii de ea, o întorci până se suprapune.
    //
    // DUPĂ AȘEZARE, PIXELUL E METRU. Ăsta e tot rostul: din clipa în care
    // foaia e calibrată, «colțul camerei e la 312, 488 în imagine» și «colțul
    // camerei e la 4,10, 6,35 în plan» sunt același lucru, iar conversia n-o
    // mai face nimeni în cap. `ruleaza_cod` primește `calc` și cele două
    // funcții ale lui (`laMetri`, `laPixeli`), deci socoteala se scrie o dată.

    {
      nume: "citeste_imaginea",
      descriere:
        "Uită-te la imaginea de sub desen (hârtia de calc) — întreagă sau "
        + "numai pe o zonă, mărită. Zona e în PIXELII imaginii, "
        + "`[x0, y0, x1, y1]`, cu (0,0) sus-stânga. Pentru ce e prea mărunt în "
        + "poza din mesaj: o cotă scrisă, un nume de cameră, grosimea unui "
        + "zid. Cere sferturi de plan, nu imaginea întreagă de zece ori.",
      parametri: {
        type: "object", additionalProperties: false,
        properties: {
          zona: {
            type: "array", items: { type: "number" },
            minItems: 4, maxItems: 4,
            description: "[x0, y0, x1, y1], în pixelii imaginii",
          },
        },
      },
      ruleaza: async ({ zona }) => {
        const c = ctx.calc?.();
        if (!c) return { eroare: "Nu e nicio imagine sub desen." };
        try {
          const d = await decupeaza(c.imagine, c.latime, c.inaltime,
                                    zona as [number, number, number, number]);
          return {
            imagine: d.url,
            continut: {
              imaginea: { latime: c.latime, inaltime: c.inaltime },
              zona: d.zona, marire: d.marire,
              calibrata: c.scara > 0,
              // Cifra din care se vede dacă o calibrare are sens: un plan de
              // apartament are între 8 și 20 de metri lățime.
              ...(c.scara > 0
                ? { metri_pe_pixel: Math.round(c.scara * 1e5) / 1e5,
                    latime_m: r2(c.latime * c.scara) }
                : {}),
            },
          };
        } catch (e) {
          return { eroare: String((e as Error)?.message ?? e) };
        }
      },
    },

    {
      nume: "aseaza_imaginea",
      descriere:
        "Așază hârtia de calc peste plan: scara, rotirea, poziția. "
        + "`scara_din` = două puncte din imagine (pixeli) și distanța "
        + "adevărată dintre ele în metri (o cotă scrisă, ori o ușă); "
        + "`directie` spune pe ce axă a planului cade segmentul, și din el "
        + "iese rotirea. `ancora` mută foaia: punctul din imagine cade pe cel "
        + "din plan. Întoarce unde ajung colțurile. Nu atinge documentul.",
      parametri: {
        type: "object", additionalProperties: false,
        properties: {
          scara_din: {
            type: "object", additionalProperties: false,
            required: ["a", "b", "distanta_m"],
            properties: {
              a: { type: "array", items: { type: "number" },
                   minItems: 2, maxItems: 2 },
              b: { type: "array", items: { type: "number" },
                   minItems: 2, maxItems: 2 },
              distanta_m: { type: "number", exclusiveMinimum: 0 },
              directie: { type: "string", enum: ["x", "y"] },
            },
          },
          ancora: {
            type: "object", additionalProperties: false,
            required: ["imagine", "plan"],
            properties: {
              imagine: { type: "array", items: { type: "number" },
                         minItems: 2, maxItems: 2 },
              plan: { type: "array", items: { type: "number" },
                      minItems: 2, maxItems: 2 },
            },
          },
          rotire_grade: { type: "number" },
          opacitate: { type: "number", minimum: 0.05, maximum: 1 },
          ascuns: { type: "boolean" },
        },
      },
      ruleaza: ({ scara_din, ancora, rotire_grade, opacitate, ascuns }) => {
        const c = ctx.calc?.();
        if (!c || !ctx.puneCalc) return { eroare: "Nu e nicio imagine sub desen." };
        let nou: Calc = { ...c };
        // Mijlocul foii, ca reper: o scară sau o rotire nouă fără ancoră o
        // lasă acolo unde era. Altfel, orice recalibrare ar fi zvârlit foaia
        // în altă parte a planșei și ar fi cerut o a doua mutare.
        const mijloc = c.scara > 0
          ? laMetri(c, c.latime / 2, c.inaltime / 2) : null;
        if (scara_din) {
          const [ax, ay] = scara_din.a as [number, number];
          const [bx, by] = scara_din.b as [number, number];
          const dpx = Math.hypot(bx - ax, by - ay);
          if (dpx < 1) {
            return { eroare: "Cele două puncte sunt în același loc: nu iese "
                             + "nicio scară din ele." };
          }
          nou.scara = scara_din.distanta_m / dpx;
          if (scara_din.directie) {
            const u = (Math.atan2(by - ay, bx - ax) * 180) / Math.PI;
            // Segmentul trebuie să ajungă pe axa cerută: rotirea e ce lipsește
            // până acolo, adusă în (-90, 90] — o poză e strâmbă cu grade, nu
            // întoarsă cu 180.
            let r = (scara_din.directie === "x" ? 0 : 90) - u;
            while (r <= -90) r += 180;
            while (r > 90) r -= 180;
            nou.rot = Math.round(r * 100) / 100;
          }
        }
        if (typeof rotire_grade === "number") nou.rot = rotire_grade;
        if (typeof opacitate === "number") nou.opacitate = opacitate;
        if (typeof ascuns === "boolean") nou.ascuns = ascuns;
        if (!nou.scara) {
          return { eroare: "Foaia n-are încă scară. Dă-i `scara_din` cu două "
                           + "puncte și distanța adevărată dintre ele." };
        }
        if (ancora) {
          const [px, py] = ancora.imagine as [number, number];
          const [mx, my] = ancora.plan as [number, number];
          const fara = { ...nou, x0: 0, y0: 0 };
          const v = laMetri(fara, px, py)!;
          nou = { ...nou, x0: r3(mx - v[0]), y0: r3(my - v[1]) };
        } else if (mijloc) {
          const fara = { ...nou, x0: 0, y0: 0 };
          const v = laMetri(fara, c.latime / 2, c.inaltime / 2)!;
          nou = { ...nou, x0: r3(mijloc[0] - v[0]), y0: r3(mijloc[1] - v[1]) };
        }
        ctx.puneCalc(nou);
        const colt = (px: number, py: number) => {
          const p = laMetri(nou, px, py)!;
          return [r2(p[0]), r2(p[1])];
        };
        return {
          rezumat: `calc ${r2(nou.latime * nou.scara)} × `
                   + `${r2(nou.inaltime * nou.scara)} m`,
          continut: {
            metri_pe_pixel: Math.round(nou.scara * 1e5) / 1e5,
            rotire_grade: nou.rot,
            latime_m: r2(nou.latime * nou.scara),
            inaltime_m: r2(nou.inaltime * nou.scara),
            colturi: {
              stanga_sus: colt(0, 0),
              dreapta_sus: colt(nou.latime, 0),
              dreapta_jos: colt(nou.latime, nou.inaltime),
              stanga_jos: colt(0, nou.inaltime),
            },
          },
        };
      },
    },

    {
      nume: "ruleaza_cod",
      nucleu: true,
      descriere:
        "Rulează JavaScript pe o COPIE a documentului, ca să calculezi. "
        + "`doc` e documentul modulului; `plan` e vederea derivată (camere, "
        + "pereți, goluri, cu aceiași indici ca la citire); `E` e API-ul "
        + "editorului (v. `api_editorului`), bun și la măsurat: "
        + "`E.cadru`, `E.osiaPeretelui`, `E.deriva`. `calc` e hârtia de calc "
        + "de sub desen, cu calibrarea ei, sau `null`: `E.laMetri(calc, px, "
        + "py)` duce un punct citit în pixelii imaginii în metrii planului, "
        + "`E.laPixeli` invers. Ultima expresie, sau ce "
        + "dai cu `return`, se întoarce ca rezultat. Copia nu se scrie "
        + "nicăieri: ca să SCHIMBI ceva, `editeaza_prin_cod`. Nu ai rețea și "
        + "nici acces la pagină.",
      parametri: {
        type: "object", additionalProperties: false, required: ["cod"],
        properties: { cod: { type: "string" } },
      },
      ruleaza: async ({ cod }) => {
        const { doc, plan } = planul();
        const r = await ruleazaCod(cod, doc, plan, { calc: ctx.calc?.() ?? null });
        return r.ok ? { continut: r.valoare } : { eroare: r.eroare };
      },
    },

    // ---- CALEA GENERALĂ ---------------------------------------------------
    //
    // CE SE POATE DIN INTERFAȚĂ SE POATE ȘI DE AICI, fără o unealtă scrisă
    // pentru fiecare gest. Uneltele de mai sus rămân scurtături — alegerea
    // «toate ușile de interior de pe latura A» scrisă ca argumente e mai
    // scurtă și mai greu de greșit decât aceeași alegere scrisă în cod —, dar
    // nu mai sunt GRANIȚA: ce nu are unealtă se face cu pârghia pe care o
    // apasă și mouse-ul.

    {
      nume: "editeaza_prin_cod",
      nucleu: true,
      descriere:
        "Schimbă modulul rulând JavaScript peste API-ul editorului — ACELEAȘI "
        + "funcții pe care le cheamă mouse-ul. E calea generală: ce se poate "
        + "face cu mâna se poate face de aici, și pentru orice n-are unealtă "
        + "proprie e singura cale. `doc` e documentul (o copie a ta), `plan` "
        + "vederea derivată, `calc` hârtia de calc de sub desen (v. "
        + "`ruleaza_cod`), `E` API-ul editorului — cere-l cu "
        + "`api_editorului` înainte, nu ghici numele. Codul întoarce "
        + "documentul NOU, de obicei un lanț de apeluri pornit din `doc`: "
        + "`return E.oglindesteUsa(E.mutaPeretele(doc, 3, [0, 0.12]), 2)`. "
        + "GEOMETRIA SE MIȘCĂ NUMAI CU PÂRGHIILE `E.…`, niciodată scriind în "
        + "`poly`, `x`, `y` sau `axis`: ele duc după ele capetele vecinilor, "
        + "golurile, prizele, mobila rezemată și camerele, iar scrisul direct "
        + "nu duce nimic și lasă zidul desenat într-un loc și măsurat în "
        + "altul. Un zid mutat de mână e refuzat, cu numele lui. Scrisul "
        + "direct rămâne pentru ce n-are geometrie: etichete, feluri, câmpuri "
        + "fără pârghie. Totul intră ca UN pas de istoric (⌘Z).",
      schimba: true,
      parametri: {
        type: "object", additionalProperties: false, required: ["cod"],
        properties: {
          cod: { type: "string" },
          ce_face: { type: "string",
                     description: "o propoziție pentru om și pentru jurnal" },
        },
      },
      ruleaza: async ({ cod, ce_face }) => {
        const inainte = ctx.doc();
        if (!inainte) return { eroare: "Nu e niciun modul deschis." };
        const { doc, plan } = planul();
        const r = await ruleazaCod(cod, doc, plan,
                                   { vreaDoc: true, calc: ctx.calc?.() ?? null });
        if (!r.ok) return { eroare: r.eroare };
        const nou = r.doc as TronsonDoc;
        // OMUL ARE MÂNA LIBERĂ cât rulează codul: firul e altul, iar o tragere
        // de perete între timp ar fi fost ștearsă de documentul întors, care
        // pornise de la starea de dinainte.
        if (ctx.doc() !== inainte) {
          return { eroare: "Documentul s-a schimbat cât rula codul (omul a "
                           + "mutat ceva). Citește din nou și încearcă iar." };
        }
        // POARTA ÎMPOTRIVA SCRISULUI DE MÂNĂ — v. `zidurileScriseDeMana`.
        // Nu se aplică NIMIC: un document pe jumătate bun e mai rău decât
        // unul neschimbat, fiindcă greșeala se vede abia peste trei pași.
        const deMana = zidurileScriseDeMana(inainte, nou);
        if (deMana.length) {
          return { eroare:
            `Zidurile ${deMana.join(", ")} au poligonul schimbat, dar axul `
            + "rămas la locul vechi — semnul unei scrieri directe în `poly`. "
            + "Așa, zidul se desenează în locul nou și se măsoară în cel "
            + "vechi (lungimea, cotele, cadrul), iar vecinii, golurile, "
            + "prizele, mobila și camerele nu-l urmează. N-am aplicat nimic. "
            + "Mută-le cu pârghiile editorului — `E.mutaPeretele`, "
            + "`E.mutaPereteleSingur`, `E.mutaCapatul`, `E.redimPeretele`, "
            + "`E.mutaLaturaCamerei` —, nu scriind în geometrie." };
        }
        ctx.aplica(() => nou, { contur: conturulSchimbat(inainte, nou) });
        const dupa = planul();
        return {
          atinse: cateAuMiscat(inainte, dupa.doc),
          rezumat: (typeof ce_face === "string" && ce_face.trim())
            ? ce_face.trim().slice(0, 200) : "editare prin cod",
          continut: {
            inainte: feliaPlanului(plan, doc as TronsonDoc, ""),
            acum: feliaPlanului(dupa.plan, dupa.doc, ""),
          },
        };
      },
    },

    {
      nume: "api_editorului",
      nucleu: true,
      descriere:
        "Pârghiile editorului, citite din sursa lui: numele, parametrii și la "
        + "ce sunt. Sunt funcțiile pe care le cheamă mouse-ul și pe care le "
        + "primești ca `E` în `ruleaza_cod` și `editeaza_prin_cod`. Fără "
        + "`caut`, numai semnăturile, toate; cu `caut` (un cuvânt sau un "
        + "tipar), ce s-a potrivit, cu explicația lui — caută în nume și în "
        + "explicații: «usa», «nucleu», «contur», «mobil».",
      parametri: {
        type: "object", additionalProperties: false,
        properties: { caut: { type: "string" } },
      },
      ruleaza: async ({ caut }) => ({ continut: await suprafata(caut) }),
    },

    {
      nume: "plan_de_lucru",
      nucleu: true,
      descriere:
        "Scrie pașii pe care îi ai de făcut, ca să-i vadă omul. NUMAI la "
        + "lucrările lungi — peste vreo cinci pași, sau când atingi zeci de "
        + "elemente. La o cerere de un pas, planul e un tur plătit ca să scrii "
        + "ce oricum faci în turul următor: fă schimbarea direct. Când chiar o "
        + "folosești, trimite lista întreagă din nou de fiecare dată, cu pașii "
        + "terminați marcați.",
      parametri: {
        type: "object", additionalProperties: false, required: ["pasi"],
        properties: {
          pasi: {
            type: "array",
            items: {
              type: "object", additionalProperties: false, required: ["text"],
              properties: {
                text: { type: "string" },
                gata: { type: "boolean" },
              },
            },
          },
        },
      },
      // Nu atinge documentul: e un panou, nu o unealtă de desen. Rezultatul e
      // chiar lista, ca modelul s-o vadă înapoi așa cum a trimis-o.
      ruleaza: ({ pasi }) => ({ continut: { pasi } }),
    },
  ];
}

/** Manifestul care pleacă spre serviciu: uneltele fără funcțiile lor. */
export function manifest(unelte: Unealta[]) {
  return unelte.map(({ nume, descriere, parametri, schimba, nucleu }) =>
    ({ nume, descriere, parametri, schimba: !!schimba, nucleu: !!nucleu }));
}
